import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import { compose } from 'recompose';
import Snackbar from '@/components/Snackbar';
import { TextField, Grid, InputAdornment, LinearProgress, MenuItem } from '@material-ui/core';
import EditOrderModal from '@/components/ShipmentForm/modals/EditOrderModal';
import Footer from './Footer';
import _, { isObject } from 'lodash';
import DispatchUnassignedColumnFilterPanel, {
    COLUMN_FILTER_MAP as DISPATCH_UNASSIGNED_COLUMN_FILTER_MAP,
} from './ColumnFilterPanel';
import { PrimaryHeaderLabel } from './blocks';
import { Search } from '@material-ui/icons';
import withFeatureFlag from '../Auth/withFeatureFlag';
import { SAAS_V1 } from '@/constants/featureFlags';
import { OnwardTabContainer, OnwardTab } from '../Tabs';
import { Context, ContextProvider } from './store';
import { MODALS, UNASSIGNED_TABS } from './constants';
import PlanningModal from './modals/PlanningModal';
import MarketplaceModal from '../ShipmentForm/modals/MarketplaceModal';
import CreditCardModal from '../ShipmentForm/modals/CreditCardModal';
import CancellationModal from '../CancellationModal';
import MultiListingsModal from './modals/MultiListingsModal';
import zipcode_to_timezone from 'zipcode-to-timezone';
import { addDays } from 'date-fns';
import { asDateInTZ, asUTCDate } from '@/utilities/convertToISO';
import { useClientUser, useOrderTableProfiles } from '@/hooks';
import { BIDDING_DISABLED } from '@/constants/featureFlags';
import CustomizableTable from '@/components/CustomizableTable';
import CustomizableTableProfileControl from '@/components/CustomizableTable/CustomizableTableProfileControl';
import { useTableColumns } from './columns';
import { useNavigate } from 'react-router';
import { validate } from '@onward-delivery/core';
import CrossdockModal from './modals/CrossdockModal';
import { UserContext } from '../App';

const DEFAULT_TABLE_INITIAL_STATE = {
    hiddenColumns: [],
    columnOrder: [],
    columnResizing: { columnWidths: {} },
};

const DISPATCH_UNASSIGNED_PROFILE_FILTER_KEYS = [
    'createdFrom',
    'createdTo',
    'selectedZips',
    'ownerType',
    'warehouseStatus',
    'hasPrefDate',
    'prefDateFrom',
    'prefDateTo',
    'search',
    'warehouseFrom',
    'warehouseTo',
    'serviceType',
    'locationType',
];

const DispatchUnassigned = () => {
    const {
        state: {
            loading,
            tab,
            modal,
            editingOrder,
            notification,
            selectedOrders,
            listings,
            orders,
            filters,
            selectedOrderIds,
        },
        callbacks,
    } = useContext(Context);
    const { user } = useContext(UserContext);
    const { circles, user_id, accountType, payment_type, stripe_payment_method } = useClientUser();

    const [searchText, setSearchText] = useState('');
    const [debouncer, setDebouncer] = useState(null);
    const [tableInitialState, setTableInitialState] = useState(() => DEFAULT_TABLE_INITIAL_STATE);
    const [tableKey, setTableKey] = useState(0);
    const [tableLayoutState, setTableLayoutState] = useState({
        hiddenColumns: [],
        columnOrder: [],
        columnWidths: {},
    });
    const [sortBy, setSortBy] = useState({ id: 'order_number', desc: true });

    const navigate = useNavigate();

    const columns = useTableColumns({
        editEnabled: tab.editEnabled,
        marketplaceEnabled: circles?.['ltl-marketplace'],
        accountType,
        user_id,
    });

    const handleSearch = (e) => {
        const input = e.target.value;
        setSearchText(input);
        if (debouncer) {
            clearTimeout(debouncer);
        }
        setDebouncer(
            setTimeout(() => {
                callbacks.setFilters((prev) => ({ ...prev, search: e.target.value }));
            }, 500)
        );
    };

    useEffect(() => {
        let _listings = {};
        selectedOrders.forEach((order) => {
            let orderTZ = order?.dropoff_zip
                ? zipcode_to_timezone.lookup(order?.dropoff_zip)
                : Intl.DateTimeFormat().resolvedOptions().timeZone;
            const utc = addDays(
                new Date(asUTCDate(new Date().toISOString()).setUTCHours(0, 0, 0, 0)),
                21
            ).toISOString();

            _listings[order.order_id] = {
                order_id: order.order_id,
                listing_type: circles?.[BIDDING_DISABLED] ? 'claim_now' : 'all',
                shipper_rate: order.shipper_rate,
                expiration_date: asDateInTZ(utc, orderTZ).toISOString(),
            };
        });
        callbacks.setListings(_listings);
    }, [selectedOrders]);

    const filterableColumnIds = useMemo(() => {
        const base = [
            'dropoff',
            'service_type',
            'warehousestatus',
            'received_date',
            'created_at',
            'preferred_delivery_date',
        ];
        if (accountType === 'carrier') {
            return [...base, 'owner'];
        }
        return base;
    }, [accountType]);

    const applyFilters = useCallback(
        (updates) => {
            callbacks.setFilters((prev) => ({ ...prev, ...updates }));
        },
        [callbacks]
    );

    const [openColumnFilter, setOpenColumnFilter] = useState(null);

    const handleOpenColumnFilter = useCallback(
        (columnId) => {
            if (filterableColumnIds.includes(columnId)) {
                setOpenColumnFilter(columnId);
            }
        },
        [filterableColumnIds]
    );

    const handleCloseColumnFilter = useCallback(() => setOpenColumnFilter(null), []);

    const handleClearColumnFilter = useCallback(
        (columnId) => {
            const config = DISPATCH_UNASSIGNED_COLUMN_FILTER_MAP[columnId];
            if (!config?.controls?.length) return;

            const resetValues = {};
            config.controls.forEach((control) => {
                if (control.type === 'dateRange' && control.keys) {
                    control.keys.forEach((k) => {
                        resetValues[k] = null;
                    });
                } else if (control.type === 'select' || control.type === 'checkbox') {
                    resetValues[control.key] = control.type === 'checkbox' ? false : null;
                } else if (control.type === 'multiSelect') {
                    resetValues[control.key] = [];
                } else if (control.type === 'serviceType') {
                    resetValues.serviceType = null;
                    resetValues.locationType = null;
                }
            });
            applyFilters(resetValues);
            if (openColumnFilter === columnId) {
                setOpenColumnFilter(null);
            }
        },
        [applyFilters, openColumnFilter]
    );

    const buildProfileSettings = useCallback(() => {
        const filterValues = {};
        DISPATCH_UNASSIGNED_PROFILE_FILTER_KEYS.forEach((key) => {
            const val = filters?.[key];
            const include =
                typeof val === 'boolean' ||
                (val !== null && val !== undefined && val !== '' && !(Array.isArray(val) && val.length === 0));
            if (include) {
                filterValues[key] = val;
            }
        });
        return {
            layout: {
                hiddenColumns: tableLayoutState.hiddenColumns || [],
                columnOrder: tableLayoutState.columnOrder || [],
                columnWidths: tableLayoutState.columnWidths || {},
            },
            sorting: {
                sortBy: sortBy?.id ? [{ id: sortBy.id, desc: !!sortBy.desc }] : [],
            },
            filters: {
                values: filterValues,
                tabValue: tab?.value,
            },
        };
    }, [filters, sortBy, tableLayoutState, tab]);

    const applyProfileSettings = useCallback(
        (settings) => {
            const layout = settings?.layout || {};
            setTableInitialState({
                hiddenColumns: layout.hiddenColumns || DEFAULT_TABLE_INITIAL_STATE.hiddenColumns,
                columnOrder: layout.columnOrder || DEFAULT_TABLE_INITIAL_STATE.columnOrder,
                columnResizing: { columnWidths: layout.columnWidths || {} },
            });
            setTableKey((k) => k + 1);

            const nextSort = settings?.sorting?.sortBy?.[0];
            if (nextSort?.id) {
                setSortBy({ id: nextSort.id, desc: !!nextSort.desc });
            } else {
                setSortBy({ id: 'order_number', desc: true });
            }

            const savedFilters = settings?.filters?.values || {};
            callbacks.setFilters((prev) => ({ ...prev, ...savedFilters }));

            const tabValue = settings?.filters?.tabValue;
            if (tabValue !== undefined && tabValue !== null) {
                const matchedTab = UNASSIGNED_TABS.find((t) => t.value === tabValue);
                if (matchedTab) {
                    callbacks.setTab(matchedTab);
                }
            }
        },
        [callbacks]
    );

    const resetToDefault = useCallback(() => {
        setTableInitialState(DEFAULT_TABLE_INITIAL_STATE);
        setTableKey((k) => k + 1);
        setSortBy({ id: 'order_number', desc: true });
    }, []);

    const {
        profiles,
        profilesLoading,
        selectedProfileId,
        currentProfileName,
        currentProfileId,
        profileSelectProps,
        handleSaveNewProfile,
        handleOverwriteProfile,
        handleDeleteCurrentProfile,
        handleImportProfileById,
        insertProfileLoading,
        updateProfileLoading,
        deleteProfileLoading,
    } = useOrderTableProfiles({
        tableType: 'DISPATCH_UNASSIGNED',
        userId: user?.user_id,
        buildProfileSettings,
        applyProfileSettings,
        onResetToDefault: resetToDefault,
        notifyError: (message) => callbacks.setNotification({ severity: 'error', message }),
    });

    const tableCallbacks = useMemo(
        () => ({
            onEditOrder: (order) => callbacks.editOrder(order),
            onClickPlan: (order) => {
                const rowId = `${order.order_id}_${order.crossdock_leg}`;
                callbacks.setSelectedOrderIds({ [rowId]: true });
                callbacks.setModal(MODALS.PLANNING);
            },
            onClickCrossdock: (order) => {
                const rowId = `${order.order_id}_${order.crossdock_leg}`;
                callbacks.setSelectedOrderIds({ [rowId]: true });
                callbacks.setModal(MODALS.CROSS_DOCK);
            },
            onClickOnward: (order) => {
                const rowId = `${order.order_id}_${order.crossdock_leg}`;
                callbacks.setSelectedOrderIds({ [rowId]: true });
                const [invalid, missingFields] = validate({ ...order, oms: false });
                if (missingFields?.length > 0) {
                    callbacks.editOrder(invalid);
                    callbacks.setNotification({
                        severity: 'warning',
                        message: 'Cannot submit to Onward, order incomplete',
                    });
                } else if (payment_type === 'Invoice' || stripe_payment_method) {
                    callbacks.setModal(circles?.[BIDDING_DISABLED] ? MODALS.MARKETPLACE : MODALS.LISTING_OPTIONS);
                } else {
                    callbacks.setModal(MODALS.CREDIT_CARD);
                }
            },
        }),
        [callbacks, circles, payment_type, stripe_payment_method]
    );

    const profileControls = useMemo(
        () => (
            <CustomizableTableProfileControl
                disabled={profilesLoading}
                tableType="DISPATCH_UNASSIGNED"
                currentProfileName={currentProfileName}
                currentProfileId={currentProfileId}
                selectedProfileId={selectedProfileId}
                onSaveNew={handleSaveNewProfile}
                onOverwrite={handleOverwriteProfile}
                onImportById={handleImportProfileById}
                onDeleteCurrent={handleDeleteCurrentProfile}
                saving={insertProfileLoading}
                overwriting={updateProfileLoading}
                deleting={deleteProfileLoading}
            />
        ),
        [
            currentProfileId,
            currentProfileName,
            deleteProfileLoading,
            handleDeleteCurrentProfile,
            handleImportProfileById,
            handleOverwriteProfile,
            handleSaveNewProfile,
            insertProfileLoading,
            profilesLoading,
            selectedProfileId,
            updateProfileLoading,
        ]
    );

    return (
        <>
            <DispatchUnassignedColumnFilterPanel
                open={Boolean(openColumnFilter)}
                columnId={openColumnFilter}
                onClose={handleCloseColumnFilter}
                applyFilters={applyFilters}
                filters={filters}
                orders={orders}
                accountType={accountType}
            />
            <Grid container direction="column" wrap="nowrap" className="h-100">
                <Grid
                    item
                    css={css`
                        display: flex;
                        justify-content: center;
                        background-color: white;
                    `}
                >
                    <OnwardTabContainer
                        value={tab.value}
                        onChange={(e, t) => callbacks.setTab(UNASSIGNED_TABS[t])}
                        textColor="primary"
                        indicatorColor="primary"
                        centered
                    >
                        {UNASSIGNED_TABS.map((tab) => (
                            <OnwardTab
                                key={`unassigned-tab-${tab.label}`}
                                label={tab.label}
                                value={tab.value}
                                style={{ outline: 0 }}
                            />
                        ))}
                    </OnwardTabContainer>
                </Grid>

                <Grid
                    item
                    css={css`
                        display: flex;
                        justify-content: space-between;
                        padding: 2rem;
                    `}
                >
                    <PrimaryHeaderLabel>Unscheduled Orders</PrimaryHeaderLabel>
                    <span
                        css={css`
                            display: flex;
                            align-items: center;
                            gap: 1rem;
                        `}
                    >
                        <TextField
                            value={searchText}
                            placeholder="Search Orders"
                            onChange={handleSearch}
                            variant="outlined"
                            color="primary"
                            size="small"
                            InputProps={{
                                style: { backgroundColor: 'white' },
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            select
                            variant="outlined"
                            size="small"
                            label={profiles?.length ? 'Profile' : 'Select Profile...'}
                            InputLabelProps={{ shrink: true }}
                            {...profileSelectProps}
                            InputProps={{ style: { backgroundColor: 'white', minWidth: 180 } }}
                        >
                            <MenuItem value="">Default</MenuItem>
                            {profiles.map((p) => (
                                <MenuItem key={p.order_table_profile_id} value={p.order_table_profile_id}>
                                    {p.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </span>
                </Grid>

                <Grid
                    item
                    css={css`
                        flex: 1 1 0;
                        min-height: 0;
                        margin: 0 2rem;
                        max-width: 100%;
                        overflow: hidden;
                        display: flex;
                        flex-direction: column;
                    `}
                >
                    {loading ? (
                        <LinearProgress
                            color="primary"
                            css={css`
                                width: 100%;
                            `}
                        />
                    ) : (
                        <CustomizableTable
                            key={tableKey}
                            data={orders || []}
                            columns={columns}
                            enableRowSelection={true}
                            selectedRowIds={selectedOrderIds}
                            onSelectionChange={callbacks.setSelectedOrderIds}
                            sortBy={sortBy ? [sortBy] : []}
                            onSortChange={(newSortBy) => {
                                const next = newSortBy || { id: 'order_number', desc: true };
                                if (next.id !== sortBy?.id || next.desc !== sortBy?.desc) {
                                    setSortBy(next);
                                }
                            }}
                            onRowClick={(order, e) => {
                                const href = `/order/${order.order_id}`;
                                if (e?.ctrlKey || e?.metaKey) {
                                    window.open(href, '_blank');
                                } else {
                                    navigate(href);
                                }
                            }}
                            getRowId={(order) => `${order.order_id}_${order.crossdock_leg}`}
                            initialState={tableInitialState}
                            onLayoutChange={setTableLayoutState}
                            wrapBodyCellContent={true}
                            manualSortBy={false}
                            cellRenderContext={{ callbacks: tableCallbacks }}
                            filterableColumns={filterableColumnIds}
                            onOpenColumnFilter={handleOpenColumnFilter}
                            onClearColumnFilter={handleClearColumnFilter}
                        />
                    )}
                </Grid>

                <Grid
                    item
                    css={css`
                        flex-shrink: 0;
                    `}
                >
                    <Footer profileControls={profileControls} />
                </Grid>

                <EditOrderModal
                    order={editingOrder}
                    open={isObject(editingOrder)}
                    opt={{
                        readOnly: !editingOrder?.oms && !!editingOrder?.carrier_invoice_id,
                        enableSingleOrderManifest: true,
                    }}
                    callbacks={{
                        onSave: callbacks.onSaveOrder,
                        onClose: () => {
                            callbacks.editOrder(null);
                        },
                        onError: (error) => {
                            callbacks.setNotification({
                                severity: 'error',
                                message: error?.message || error || `We're sorry, something went wrong.`,
                            });
                        },
                    }}
                />

                <PlanningModal
                    orderCount={selectedOrders?.length}
                    open={modal === MODALS.PLANNING}
                    onClose={() => callbacks.setModal(null)}
                />
                <CrossdockModal
                    orderCount={selectedOrders?.length}
                    open={modal === MODALS.CROSS_DOCK}
                    onClose={() => callbacks.setModal(null)}
                />
                <MarketplaceModal
                    open={modal === MODALS.MARKETPLACE}
                    onClose={() => callbacks.setModal(null)}
                    onSubmit={callbacks.sendToMarketplace}
                    orders={selectedOrders}
                    listings={listings}
                />
                <MultiListingsModal open={modal === MODALS.LISTING_OPTIONS} onClose={() => callbacks.setModal(null)} />
                <CreditCardModal
                    open={modal === MODALS.CREDIT_CARD}
                    handleClose={() => callbacks.setModal(null)}
                    onSuccess={() => {
                        callbacks.setNotification({
                            severity: 'success',
                            message: 'Card added successfully. You may now send your orders to the Onward Marketplace!',
                        });
                        callbacks.setModal(MODALS.MARKETPLACE);
                    }}
                />
                <CancellationModal
                    orders={selectedOrders}
                    open={modal === MODALS.CANCELLATION}
                    onClose={() => callbacks.setModal(null)}
                    ordersCancelledCallback={(ordersCancelled, error) => {
                        if (error) {
                            callbacks.setNotification({
                                severity: 'error',
                                message: 'Error cancelling orders. Please try again.',
                            });
                        } else {
                            callbacks.setNotification({
                                severity: 'success',
                                message: `Succesfully cancelled ${ordersCancelled.length} orders.`,
                            });
                            callbacks.refetch();
                        }
                    }}
                />

                <Snackbar
                    severity={notification?.severity}
                    open={Boolean(notification?.message)}
                    message={notification?.message}
                    handleClose={() => callbacks.setNotification(null)}
                    customAction={notification?.action}
                />
            </Grid>
        </>
    );
};

const withContext = (Component) => (props) =>
    (
        <ContextProvider>
            <Component {...props} />
        </ContextProvider>
    );

export default compose(withFeatureFlag(SAAS_V1), withContext)(DispatchUnassigned);
